import React from 'react';
import { Link } from 'react-router-dom';
import SEO, { softwareAppSchema } from '../../Components/SEO';
import SEOPageLayout from '../../Components/SEOPageLayout';

export default function ForStudents() {
  return (
    <SEOPageLayout>
      <SEO
        title="CheckScript for Students — AI Study Assistant for Exams & Assignments"
        description="Transform how you study with CheckScript AI. Get instant explanations for mock tests, capture study materials from any app, and ace your exams."
        keywords="AI study assistant, exam preparation tool, student AI tool, AI homework helper, CheckScript for students"
        path="/use-cases/students"
        structuredData={softwareAppSchema()}
      />

      <section className="seo-hero seo-animate">
        <div className="seo-mesh-bg"></div>
        <div className="seo-container seo-container--sm">
          <div className="seo-hero-badge">🎓 For Students</div>
          <h1 className="seo-h1">
            Your Ultimate <span className="seo-gradient-text">AI Study Companion</span>
          </h1>
          <p className="seo-lead">
            From late-night coding assignments to high-stakes mock exams, CheckScript AI provides the real-time assistance you need to learn faster and score higher.
          </p>
          <div className="seo-hero-actions">
            <Link to="/download" className="seo-btn seo-btn--primary">Download Free</Link>
            <Link to="/pricing" className="seo-btn seo-btn--secondary">View Student Plans</Link>
          </div>
        </div>
      </section>

      <section className="seo-section seo-section--alt seo-animate">
        <div className="seo-container">
          <div className="seo-bento-grid" style={{ alignItems: 'center', gap: '48px' }}>
            <div>
              <h2 className="seo-h2">A Day in the Life with CheckScript</h2>
              <p className="seo-text-secondary seo-mb-24">
                Imagine taking a complex practice test. You encounter a tough algorithm question. Instead of spending 20 minutes Googling the concept, you press <code>Alt+O</code>. Instantly, CheckScript captures the question, analyzes it, and provides a detailed step-by-step breakdown of the solution right on your screen.
              </p>
              <ul className="seo-feature-list">
                <li>Capture un-savable mock test questions for review</li>
                <li>Get instant explanations for complex topics</li>
                <li>Debug computer science assignments in real-time</li>
                <li>Study without internet using local Neural Driver</li>
              </ul>
            </div>
            <div className="seo-glass-card">
              <h3 className="seo-h3 seo-mb-24">Supported Platforms</h3>
              <p className="seo-text-secondary seo-mb-16">CheckScript's robust screen capture works alongside standard educational platforms:</p>
              <div className="seo-bento-grid" style={{ gap: '16px' }}>
                <div style={{ background: 'rgba(99,102,241,0.1)', padding: '12px', borderRadius: '8px', textAlign: 'center', fontSize: '0.9rem' }}>Canvas / Moodle</div>
                <div style={{ background: 'rgba(99,102,241,0.1)', padding: '12px', borderRadius: '8px', textAlign: 'center', fontSize: '0.9rem' }}>CodeTantra</div>
                <div style={{ background: 'rgba(99,102,241,0.1)', padding: '12px', borderRadius: '8px', textAlign: 'center', fontSize: '0.9rem' }}>Blackboard</div>
                <div style={{ background: 'rgba(99,102,241,0.1)', padding: '12px', borderRadius: '8px', textAlign: 'center', fontSize: '0.9rem' }}>Mercer Mettl</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="seo-section seo-animate">
        <div className="seo-container">
          <h2 className="seo-h2 seo-text-center seo-mb-48">How to Use CheckScript for Exams</h2>
          <div className="seo-bento-grid">
            <div className="seo-bento-box seo-bento-square">
              <div className="seo-card-icon seo-card-icon--cyan">1</div>
              <h3>Launch in Background</h3>
              <p>Start CheckScript before your study session. It runs silently in the system tray, consuming minimal resources.</p>
            </div>
            <div className="seo-bento-box seo-bento-square">
              <div className="seo-card-icon seo-card-icon--emerald">2</div>
              <h3>Trigger the Hotkey</h3>
              <p>When stuck, press your configured hotkey. CheckScript instantly captures the active problem from your screen.</p>
            </div>
            <div className="seo-bento-box seo-bento-square">
              <div className="seo-card-icon seo-card-icon--amber">3</div>
              <h3>Learn and Progress</h3>
              <p>Review the AI-generated explanation, apply the knowledge, and move forward with confidence.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="seo-section seo-section--alt seo-animate">
        <div className="seo-container seo-container--sm">
          <h2 className="seo-h2 seo-text-center seo-mb-48">Student Success Stories</h2>
          <div className="seo-bento-grid" style={{ gap: '24px' }}>
            <div className="seo-testimonial">
              <div className="seo-testimonial-stars">★★★★★</div>
              <p className="seo-testimonial-text">"CheckScript changed how I prep for my CS exams. Being able to instantly debug snippets from past papers without retyping them is a massive time-saver."</p>
              <div className="seo-testimonial-author">
                <div className="seo-testimonial-avatar">A</div>
                <div>
                  <div className="seo-testimonial-name">Arjun K.</div>
                  <div className="seo-testimonial-role">Computer Science Student</div>
                </div>
              </div>
            </div>
            <div className="seo-testimonial">
              <div className="seo-testimonial-stars">★★★★★</div>
              <p className="seo-testimonial-text">"The Neural Driver is amazing. I can study on the train without WiFi, and the local AI is just as smart as ChatGPT."</p>
              <div className="seo-testimonial-author">
                <div className="seo-testimonial-avatar">P</div>
                <div>
                  <div className="seo-testimonial-name">Priya S.</div>
                  <div className="seo-testimonial-role">Engineering Undergrad</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="seo-cta seo-animate">
        <div className="seo-container">
          <div className="seo-cta-box">
            <h2 className="seo-h2">Ready to Boost Your Grades?</h2>
            <p className="seo-text-secondary seo-mb-32">Join thousands of students studying smarter with CheckScript AI.</p>
            <Link to="/download" className="seo-btn seo-btn--primary">Download Free Trial</Link>
          </div>
        </div>
      </section>
    </SEOPageLayout>
  );
}
